import {useMutation, useQuery, useQueryClient} from "react-query";
import axios from "axios";
import {PagedSearchParams} from "@atiautomacao/ati-ui-library";
import {useSnackbar} from "notistack";
import {StatusColorProps} from "../../../Shared/Types/StatusColorProps";

const apiUrl = "api/status-colors";

// Função para salvar ou atualizar um StatusColor
const saveStatusColor = async (data: StatusColorProps) => {
    try {
        if (data.id != null) {
            return await axios.put(`${apiUrl}/${data.id}`, data, {timeout: 30000}).then((res) => res.data);
        } else {
            return await axios.post(apiUrl, data, {timeout: 30000}).then((res) => res.data);
        }
    } catch (error) {
        console.error("Error saving data:", error);
        return Promise.reject(error);
    }
};

export const SaveStatusColorReactQuery = () => {
    const {enqueueSnackbar, closeSnackbar} = useSnackbar();

    return useMutation({
        mutationFn: (data: any) => saveStatusColor(data),
        onSuccess: () => {
            closeSnackbar();
            enqueueSnackbar("Status de cor salvo com sucesso!", {variant: "success"});
        },
        onError: (error: any) => {
            if(error?.response.status === 409 && error?.response?.data?.title?.includes("Already exists")){
                enqueueSnackbar(error?.response?.data?.detail, { variant: "error", autoHideDuration: 1500 });
                return
            }
            if(error?.response.status === 503){
                enqueueSnackbar("Serviço indisponível", { variant: "error", autoHideDuration: 1500 });
                return
            }
            const errorMessages = error?.response?.data?.detail.split('/').filter((s:any) => s.trim() !== '') ?? ["Erro desconhecido. Informe ao suporte."]
            const result = errorMessages.map((erro:any) => {
                return erro.replace(/^.*?:\s*/, "")
            })

            result.forEach((message:any) => {
                enqueueSnackbar(`${message}`, { variant: "error", autoHideDuration: 2000 });
            })

        }
    });
};

// Função para buscar StatusColors com paginação
const fetchStatusColors = async (pagedSearchParams: PagedSearchParams) => {
    try {
        return await axios
            .get(`${apiUrl}/search`, {
                params: pagedSearchParams ? pagedSearchParams.toURLSearchParams() : null,
                timeout: 30000
            })
            .then((res) => res.data);
    } catch (error) {
        console.error("Error fetching data:", error);
        return Promise.reject(error);
    }
};

export const StatusColorReactQuery = (pagedSearchParams: PagedSearchParams) => {
    return useQuery({
        queryKey: ["status-colors", pagedSearchParams],
        queryFn: () => fetchStatusColors(pagedSearchParams)
    });
};

// Função para buscar um StatusColor por ID
const fetchStatusColorById = async (id: number) => {
    try {
        return await axios.get(`${apiUrl}/${id}`, {timeout: 30000}).then((res) => res.data);
    } catch (error) {
        console.error("Error fetching data:", error);
        return Promise.reject(error);
    }
};

export const FindStatusColorByIdReactQuery = () => {
    const {enqueueSnackbar, closeSnackbar} = useSnackbar();

    return useMutation({
        mutationFn: (id: number) => fetchStatusColorById(id),
        onError: (error: any) => {
            closeSnackbar();
            enqueueSnackbar(`Erro ao carregar status de cor: ${error.message}`, {variant: "error"});
        }
    });
};

// Função para deletar um StatusColor
const deleteStatusColor = async (id: number) => {
    try {
        return await axios.delete(`${apiUrl}/${id}`, {timeout: 30000}).then((res) => res.data);
    } catch (error) {
        console.error("Error deleting data:", error);
        return Promise.reject(error);
    }
};

export const DeleteStatusColorReactQuery = (pagedSearchParams: PagedSearchParams) => {
    const {enqueueSnackbar, closeSnackbar} = useSnackbar();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: any) => deleteStatusColor(data.id),
        onSuccess: () => {
            closeSnackbar();
            enqueueSnackbar("Status de cor excluído com sucesso!", {variant: "success"});
            if (pagedSearchParams) {
                queryClient.invalidateQueries(["status-colors", pagedSearchParams]);
            }
        },
        onError: (error: any) => {
            closeSnackbar();
            enqueueSnackbar(`Erro ao excluir status de cor: ${error.message}`, {variant: "error"});
        }
    });
};

// Função para buscar todos os StatusColors
const fetchAllStatusColors = async () => {
    try {
        return await axios.get(`${apiUrl}/find-all`).then((res) => res.data);
    } catch (error) {
        console.error("Error fetching data:", error);
        throw error;
    }
};

export const FindAllStatusColorsReactQuery = () => {
    return useQuery(
        "find-all-status-colors", // Chave para cache e invalidação
        fetchAllStatusColors       // Função de busca
    );
};